import { validateImageFile } from '@/lib/upload'
import { cn } from '@/lib/utils'
import { AlertCircle, Upload, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Button } from './ui/button'

export interface ImageFile {
  id: string
  file: File
  preview: string
}

export interface ImageUploadProps {
  maxImages?: number
  onFilesChange?: (files: ImageFile[]) => void
  className?: string
}

export function ImageUpload({
  maxImages = 5,
  onFilesChange,
  className,
}: ImageUploadProps) {
  const [imageFiles, setImageFiles] = useState<ImageFile[]>([])
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    return () => {
      imageFiles.forEach(imageFile => {
        URL.revokeObjectURL(imageFile.preview)
      })
    }
  }, [imageFiles])

  const handleFileSelect = (files: FileList | null) => {
    if (!files || files.length === 0) return

    const remainingSlots = maxImages - imageFiles.length
    if (remainingSlots <= 0) {
      setError(`最大${maxImages}枚までアップロード可能です`)
      return
    }

    const filesToAdd = Array.from(files).slice(0, remainingSlots)

    const validFiles: ImageFile[] = []
    for (const file of filesToAdd) {
      const validationError = validateImageFile(file)
      if (validationError) {
        setError(validationError)
        continue
      }

      const imageFile: ImageFile = {
        id: `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
        file,
        preview: URL.createObjectURL(file)
      }
      validFiles.push(imageFile)
    }

    if (validFiles.length === 0) return

    setError(null)

    const updatedFiles = [...imageFiles, ...validFiles]
    setImageFiles(updatedFiles)
    onFilesChange?.(updatedFiles)

    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleRemoveImage = (id: string) => {
    const imageToRemove = imageFiles.find((img) => img.id === id)
    if (imageToRemove) {
      URL.revokeObjectURL(imageToRemove.preview)
    }

    const updatedFiles = imageFiles.filter((img) => img.id !== id)
    setImageFiles(updatedFiles)
    onFilesChange?.(updatedFiles)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    handleFileSelect(e.dataTransfer.files)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
  }

  const canUploadMore = imageFiles.length < maxImages

  return (
    <div className={cn('space-y-4', className)}>
      {/* アップロードエリア */}
      {canUploadMore && (
        <div
          className={cn(
            'relative rounded-2xl border-2 border-dashed border-pastel-purple/30',
            'bg-white/50 p-8 text-center transition-colors',
            'hover:border-pastel-purple/50 hover:bg-pastel-purple/5'
          )}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          <input
            ref={fileInputRef}
            type="file"
            aria-label="画像を選択"
            accept="image/jpeg,image/png,image/gif,image/webp"
            multiple
            className="hidden"
            onChange={(e) => handleFileSelect(e.target.files)}
          />

          <div className="flex flex-col items-center gap-4">
            <div className="rounded-full bg-pastel-purple/10 p-4">
              <Upload className="h-8 w-8 text-pastel-purple" />
            </div>

            <div>
              <p className="text-lg font-medium text-gray-700">
                写真をドラッグ&ドロップ
              </p>
              <p className="mt-1 text-sm text-gray-500">
                または
              </p>
            </div>

            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              className="rounded-full border-2 border-pastel-purple/30 hover:bg-pastel-purple/10"
            >
              ファイルを選択
            </Button>

            <p className="text-xs text-gray-500">
              JPEG, PNG, GIF, WebP（最大10MB）
              <br />
              残り{maxImages - imageFiles.length}枚アップロード可能
            </p>
          </div>
        </div>
      )}

      {/* エラー表示 */}
      {error && (
        <div className="flex items-center gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-600">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* 選択済み画像一覧 */}
      {imageFiles.length > 0 && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
          {imageFiles.map((imageFile) => (
            <div
              key={imageFile.id}
              className="group relative overflow-hidden rounded-lg border-2 border-gray-200 bg-white"
            >
              <img
                src={imageFile.preview}
                alt="アップロード画像"
                className="aspect-square w-full object-cover"
              />

              <button
                type="button"
                onClick={() => handleRemoveImage(imageFile.id)}
                className="absolute right-1 top-1 rounded-full bg-red-500 p-1 text-white opacity-0 transition-opacity hover:bg-red-600 group-hover:opacity-100"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* 上限到達メッセージ */}
      {!canUploadMore && (
        <div className="rounded-lg bg-pastel-purple/10 p-4 text-center">
          <p className="text-sm">
            最大{maxImages}枚の写真をアップロードしました
          </p>
        </div>
      )}
    </div>
  )
}
